/**
 * QuietField — a scalar that is already an input.
 *
 * THERE IS NO EDIT MODE, NO EDIT BUTTON AND NO SHEET. The value at rest IS the control: it looks
 * like text until it is hovered or focused, and typing into it is the whole interaction. That is
 * the pattern the owner asked for by name ("kill the tap-edit-button-open-sheet pattern"), and the
 * hover-revealed pencil it replaces was worse than slow — it was invisible, because
 * `opacity-0 group-hover:opacity-100` is nothing at all on a touch screen.
 *
 * SCOPED TO THE FINANCIALS CANVAS THIS PR, deliberately. The full primitive set (Money, Date,
 * Number, Select, FieldGroup, Upload, PickerPopover) and the removal of the fourteen sheet mounts
 * are PR3; what lands here is the one canvas where the interaction has to be right because the
 * value is being read inside the sentence it prints as.
 *
 * NOTHING AUTOSAVES. A dirty field marks its block and the block's commit bar is the only thing
 * that writes — these values print onto legal instruments, so confirmation stays explicit.
 */

import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

interface QuietFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
    /** The DOM id a manifest row's `jump.focus` names. */
    id: string;
    value: string;
    onValueChange: (value: string) => void;
    /** The format hint that appears on focus: `MMM d, yyyy`, `km`, `prints on the HPA`. */
    format?: string;
    /** The server's own refusal for this field, verbatim. */
    error?: string | null;
    /** Rendered inside a clause sentence rather than as a labelled row. */
    inline?: boolean;
    /** Widens the inline variant to fit a date. */
    wide?: boolean;
}

/**
 * The input alone — for the inline variant, which lives inside a `<p>` and must not open a row.
 */
export const QuietInput = forwardRef<HTMLInputElement, QuietFieldProps>(function QuietInput(
    { id, value, onValueChange, format, error, inline, wide, className, ...rest },
    ref,
) {
    return (
        <>
            <input
                {...rest}
                ref={ref}
                id={id}
                className={cn(
                    inline ? 'sd-qf-inline' : 'sd-qf',
                    inline && wide && 'sd-qf-w14',
                    className,
                )}
                value={value}
                onChange={(event) => onValueChange(event.target.value)}
                aria-invalid={error ? true : undefined}
                aria-errormessage={error ? `${id}-err` : undefined}
                autoComplete="off"
            />
            {format && <span className={inline ? 'sd-inline-fmt' : 'sd-fmt'}>{format}</span>}
        </>
    );
});

/**
 * A labelled row whose value is the control.
 *
 * THE DIRTY MARK IS A 2px RULE IN THE MARGIN, not a highlighted background: an admin scanning a
 * block for what they have changed is looking down the left edge, and a filled row is louder than
 * the value it contains.
 */
export default function QuietField({
    label,
    dirty,
    ...field
}: QuietFieldProps & { label: string; dirty?: boolean }) {
    const { id, error } = field;
    return (
        <div className={cn('sd-fld', dirty && 'sd-dirty', error && 'sd-errored')}>
            <label className="sd-fld-k" htmlFor={id}>
                {label}
            </label>
            <div className="flex min-w-0 items-center gap-2">
                <QuietInput {...field} />
            </div>
            {error && (
                <p className="sd-f-err" id={`${id}-err`}>
                    {error}
                </p>
            )}
        </div>
    );
}
